<template>
    <div class="bg-color rounded-5 p-5">
        <div class="w-100 rounded-4">
            <!-- Header -->
            <div
                class="d-flex justify-content-between"
                style="background: rgb(241, 247, 255); border-radius: 27px 27px 0 0"
            >
                <div class="flex align-items-center list-page-header">
                    <h1 class="px-5 py-3 page-heading">Chatbot Configuration</h1>
                </div>
            </div>

            <div class="w-100 bg-white" v-if="loading">
                <div class="d-grid justify-content-center align-items-center py-5" style="min-height: 400px">
                    <h4>Loading configuration...</h4>
                </div>
            </div>

            <div v-else class="bg-white w-100 px-4 py-4">
                <!-- Tabs Navigation -->
                <div class="tabs-wrapper mb-4">
                    <div class="tabs-nav">
                        <button
                            class="tab-btn"
                            :class="{ active: activeTab === 'ui' }"
                            @click="activeTab = 'ui'"
                        >
                            Chatbot UI
                        </button>
                        <button
                            class="tab-btn"
                            :class="{ active: activeTab === 'knowledge' }"
                            @click="activeTab = 'knowledge'"
                        >
                            Chatbot Knowledge
                        </button>

                    </div>
                </div>

                <!-- Two Column Layout -->
                <div class="main-content-wrapper">
                    <!-- Left Column - Configuration -->
                    <div class="config-column">
                        
                        <!-- TAB 1: Chatbot UI -->
                        <div v-show="activeTab === 'ui'">
                            <!-- Basic Info -->
                            <div class="config-section">
                                <div class="field-row">
                                    <div class="field-col">
                                        <label class="form-label fw-bold">Chatbot Name <span class="text-danger">*</span></label>
                                        <input
                                            type="text"
                                            class="form-control"
                                            v-model="chatbotConfig.name"
                                            placeholder="Enter chatbot name"
                                            required
                                        />
                                    </div>
                                    <div class="field-col">
                                        <label class="form-label fw-bold">Welcome Title</label>
                                        <input
                                            type="text"
                                            class="form-control"
                                            v-model="chatbotConfig.welcomeMessage.title"
                                            placeholder="Welcome to our support!"
                                        />
                                    </div>
                                </div>
                                <div class="field-row mt-3 mb-5">
                                    <div class="field-col">
                                        <div class="d-flex align-items-center justify-content-between border rounded px-3 py-2 h-100">
                                            <span>
                                                <strong class="d-block">Enable on Mobex Dashboard</strong>
                                                <small class="text-muted">Show the chatbot in the web PM app.</small>
                                            </span>
                                            <Toggle
                                                class="toggle-switch ms-3"
                                                labelClass="switch"
                                                spanClass="slider"
                                                id="chatbot-enable-dashboard"
                                                name="chatbot-enable-dashboard"
                                                v-model="chatbotConfig.enabledDashboard"
                                            />
                                        </div>
                                    </div>
                                    <div class="field-col">
                                        <div class="d-flex align-items-center justify-content-between border rounded px-3 py-2 h-100">
                                            <span>
                                                <strong class="d-block">Enable on Mobile Application</strong>
                                                <small class="text-muted">Show the chatbot in the mobile app.</small>
                                            </span>
                                            <Toggle
                                                class="toggle-switch ms-3"
                                                labelClass="switch"
                                                spanClass="slider"
                                                id="chatbot-enable-mobile"
                                                name="chatbot-enable-mobile"
                                                v-model="chatbotConfig.enabledMobile"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <!-- Chatbot Styling Section -->
                            <div class="bg-white w-100 rounded-4 border config-section">
                                <h2
                                    class="px-4 py-3 section-heading d-flex justify-content-between align-items-center"
                                    @click="toggleSection('styling')"
                                >
                                    <span>Chatbot Styling</span>
                                    <span class="toggle-icon">{{ expandedSections.styling ? '−' : '+' }}</span>
                                </h2>
                                <div class="p-4" v-show="expandedSections.styling">
                                    <div class="styling-grid">
                                        <div class="styling-field">
                                            <label class="form-label">Primary Color</label>
                                            <div class="color-input-wrapper">
                                                <input
                                                    type="color"
                                                    class="form-control form-control-color"
                                                    v-model="chatbotConfig.styling.primaryColor"
                                                />
                                                <input
                                                    type="text"
                                                    class="form-control"
                                                    v-model="chatbotConfig.styling.primaryColor"
                                                    placeholder="#5057c3"
                                                />
                                            </div>
                                        </div>
                                        <div class="styling-field">
                                            <label class="form-label">Secondary Color</label>
                                            <div class="color-input-wrapper">
                                                <input
                                                    type="color"
                                                    class="form-control form-control-color"
                                                    v-model="chatbotConfig.styling.secondaryColor"
                                                />
                                                <input
                                                    type="text"
                                                    class="form-control"
                                                    v-model="chatbotConfig.styling.secondaryColor"
                                                    placeholder="#f1f7ff"
                                                />
                                            </div>
                                        </div>
                                        <div class="styling-field">
                                            <label class="form-label">Text Color</label>
                                            <div class="color-input-wrapper">
                                                <input
                                                    type="color"
                                                    class="form-control form-control-color"
                                                    v-model="chatbotConfig.styling.textColor"
                                                />
                                                <input
                                                    type="text"
                                                    class="form-control"
                                                    v-model="chatbotConfig.styling.textColor"
                                                    placeholder="#333333"
                                                />
                                            </div>
                                        </div>
                                        <div class="styling-field">
                                            <label class="form-label">Font Family</label>
                                            <select class="form-control" v-model="chatbotConfig.styling.fontFamily">
                                                <option value="Inter, sans-serif">Inter</option>
                                                <option value="Roboto, sans-serif">Roboto</option>
                                                <option value="Open Sans, sans-serif">Open Sans</option>
                                                <option value="Lato, sans-serif">Lato</option>
                                                <option value="Arial, sans-serif">Arial</option>
                                            </select>
                                        </div>
                                        <div class="styling-field">
                                            <label class="form-label">Font Size</label>
                                            <select class="form-control" v-model="chatbotConfig.styling.fontSize">
                                                <option value="12px">Small (12px)</option>
                                                <option value="14px">Medium (14px)</option>
                                                <option value="16px">Large (16px)</option>
                                            </select>
                                        </div>
                                        <div class="styling-field">
                                            <label class="form-label">Border Radius</label>
                                            <select class="form-control" v-model="chatbotConfig.styling.borderRadius">
                                                <option value="0px">None</option>
                                                <option value="5px">Small</option>
                                                <option value="10px">Medium</option>
                                                <option value="20px">Large</option>
                                            </select>
                                        </div>
                                        <div class="styling-field">
                                            <label class="form-label">Chat Window Position</label>
                                            <select class="form-control" v-model="chatbotConfig.styling.position">
                                                <option value="bottom-right">Bottom Right</option>
                                                <option value="bottom-left">Bottom Left</option>
                                            </select>
                                        </div>
                                        <div class="styling-field">
                                            <label class="form-label">Chat Button Icon</label>
                                            <select class="form-control" v-model="chatbotConfig.styling.buttonIcon">
                                                <option value="chat">Chat Bubble</option>
                                                <option value="message">Message</option>
                                                <option value="help">Help</option>
                                                <option value="support">Support</option>
                                            </select>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <!-- Bot Avatar Section -->
                            <div class="bg-white w-100 rounded-4 border config-section">
                                <h2
                                    class="px-4 py-3 section-heading d-flex justify-content-between align-items-center"
                                    @click="toggleSection('avatar')"
                                >
                                    <span>Bot Avatar</span>
                                    <span class="toggle-icon">{{ expandedSections.avatar ? '−' : '+' }}</span>
                                </h2>
                                <div class="p-4" v-show="expandedSections.avatar">
                                    <label class="form-label">Bot Avatar URL</label>
                                    <div class="avatar-input-row">
                                        <input
                                            type="text"
                                            class="form-control"
                                            :value="chatbotConfig.welcomeMessage.avatarUrl"
                                            placeholder="https://example.com/avatar.png"
                                            @input="onAvatarInput($event.target.value)"
                                        />
                                        <button
                                            type="button"
                                            class="avatar-btn clear-btn"
                                            v-if="chatbotConfig.welcomeMessage.avatarUrl"
                                            @click="clearAvatar()"
                                        >
                                            Clear
                                        </button>
                                    </div>
                                    <small class="text-muted d-block mt-1">
                                        Recommended: square image, 128 × 128 px (PNG, JPG, or SVG).
                                        Leave blank to use the default chatbot icon.
                                    </small>
                                </div>
                            </div>
                        </div>

                        <!-- TAB 2: Chatbot Knowledge -->
                        <div v-show="activeTab === 'knowledge'">
                            <!-- Knowledge Sub-Tabs -->
                            <div class="sub-tabs-wrapper mb-4">
                                <div class="sub-tabs-nav">
                                    <button
                                        class="sub-tab-btn"
                                        :class="{ active: knowledgeTab === 'website' }"
                                        @click="knowledgeTab = 'website'"
                                    >
                                        Website Knowledge Base
                                    </button>
                                    <button
                                        class="sub-tab-btn"
                                        :class="{ active: knowledgeTab === 'dashboards' }"
                                        @click="knowledgeTab = 'dashboards'"
                                    >
                                        Dashboard Knowledge
                                    </button>
                                    <button
                                        class="sub-tab-btn"
                                        :class="{ active: knowledgeTab === 'faqs' }"
                                        @click="knowledgeTab = 'faqs'"
                                    >
                                        FAQs
                                    </button>
                                    <button
                                        class="sub-tab-btn"
                                        :class="{ active: knowledgeTab === 'documentation' }"
                                        @click="knowledgeTab = 'documentation'"
                                    >
                                        Documentation
                                    </button>
                                </div>
                            </div>

                            <!-- Website Knowledge Base Sub-Tab -->
                            <div v-show="knowledgeTab === 'website'">
                                <KbCrawlPanel
                                    :scrape-frequency="chatbotConfig.scrapeFrequency"
                                    :last-scraped-at="chatbotConfig.lastScrapedAt"
                                    @saved="onCrawlSaved"
                                />
                            </div>

                            <!-- Dashboard Knowledge Sub-Tab -->
                            <!-- Rendered only once opened: the panel fetches on mount,
                                 and there's no reason to hit the API for a tab the
                                 admin may never look at. -->
                            <div v-if="knowledgeTab === 'dashboards'">
                                <DashboardKnowledgePanel />
                            </div>

                            <!-- FAQs Sub-Tab -->
                            <div v-show="knowledgeTab === 'faqs'" class="bg-white w-100 rounded-4 border config-section">
                                <div class="px-4 py-3 section-heading">
                                    <span>FAQs (Frequently Asked Questions)</span>
                                </div>
                                <div class="p-4">
                                    <p class="text-muted mb-3">Add frequently asked questions that will be shown to users.</p>
                                    <div
                                        v-for="(faq, index) in chatbotConfig.faqs"
                                        :key="index"
                                        class="faq-item mb-3 p-3 border rounded"
                                    >
                                        <div class="d-flex justify-content-between align-items-start mb-2">
                                            <strong>FAQ {{ index + 1 }}</strong>
                                            <button class="btn-remove" @click="removeFaq(index)">
                                                <img src="@/assets/px/delete-icon.png" alt="Delete" style="height: 16px" />
                                            </button>
                                        </div>
                                        <div class="mb-2">
                                            <label class="form-label">Question</label>
                                            <input
                                                type="text"
                                                class="form-control"
                                                v-model="faq.question"
                                                placeholder="Enter question"
                                            />
                                        </div>
                                        <div>
                                            <label class="form-label">Answer</label>
                                            <textarea
                                                class="form-control"
                                                rows="2"
                                                v-model="faq.answer"
                                                placeholder="Enter answer"
                                            ></textarea>
                                        </div>
                                    </div>
                                    <p v-if="!chatbotConfig.faqs.length" class="text-muted text-center py-3 mb-3">
                                        No FAQs added yet. Click below to add your first FAQ.
                                    </p>
                                    <button class="btn-add" @click="addFaq">
                                        <img src="@/assets/px/add.png" alt="Add" style="height: 16px" /> Add FAQ
                                    </button>
                                </div>
                            </div>

                            <!-- Documentation Sub-Tab -->
                            <div v-show="knowledgeTab === 'documentation'" class="bg-white w-100 rounded-4 border config-section">
                                <div class="px-4 py-3 section-heading">
                                    <span>Documentation</span>
                                </div>
                                <div class="p-4">
                                    <p class="text-muted mb-3">
                                        Upload a PDF / .txt / .md file or paste a URL.
                                     </p>

                                    <!-- Saved docs list -->
                                    <div
                                        v-for="doc in chatbotConfig.documentation"
                                        :key="doc.id"
                                        class="doc-item mb-3 p-3 border rounded"
                                    >
                                        <div class="d-flex justify-content-between align-items-start mb-2">
                                            <div class="d-flex align-items-center gap-2">
                                                <strong>{{ doc.title || 'Untitled' }}</strong>
                                                <span class="badge badge-doc-type">{{ docTypeLabel(doc) }}</span>
                                                <span :class="['badge', statusBadgeClass(doc.status)]">{{ doc.status }}</span>
                                            </div>
                                            <button class="btn-remove" :disabled="doc._busy" @click="removeSavedDoc(doc)">
                                                <img src="@/assets/px/delete-icon.png" alt="Delete" style="height: 16px" />
                                            </button>
                                        </div>

                                        <div class="row mb-2">
                                            <div class="col-md-8">
                                                <label class="form-label">Title</label>
                                                <input
                                                    type="text"
                                                    class="form-control"
                                                    v-model="doc.title"
                                                    @change="saveDocTitle(doc)"
                                                    :disabled="doc._busy"
                                                />
                                            </div>
                                            <div class="col-md-4 d-flex align-items-end">
                                                <a
                                                    v-if="doc.url"
                                                    :href="doc.url"
                                                    target="_blank"
                                                    rel="noopener"
                                                    class="btn btn-outline-secondary btn-sm"
                                                >
                                                    {{ doc.type === 'url' ? 'Open link' : 'View file' }}
                                                </a>
                                            </div>
                                        </div>

                                        <div class="text-muted small">
                                            <template v-if="doc.fileName">
                                                File: {{ doc.fileName }}
                                                <span v-if="doc.fileSize"> · {{ formatBytes(doc.fileSize) }}</span>
                                            </template>
                                            <template v-else-if="doc.type === 'url'">
                                                {{ doc.url }}
                                            </template>
                                            <div v-if="doc.extractedLength" class="mt-1">
                                                {{ doc.extractedLength.toLocaleString() }} characters indexed for chatbot search.
                                            </div>
                                        </div>
                                    </div>

                                    <p v-if="!chatbotConfig.documentation.length" class="text-muted text-center py-3 mb-3">
                                        No documents added yet. Use one of the options below to add your first document.
                                    </p>

                                    <!-- Add-new picker -->
                                    <div class="add-doc-panel border rounded p-3 mt-3">
                                        <div class="d-flex gap-2 mb-3 flex-wrap">
                                            <button
                                                v-for="mode in ['file','url']"
                                                :key="mode"
                                                :class="['btn', 'btn-sm', addDocMode === mode ? 'btn-primary' : 'btn-outline-secondary']"
                                                @click="addDocMode = mode"
                                            >
                                                {{ mode === 'file' ? 'Upload file' : 'Add link' }}
                                            </button>
                                        </div>

                                        <div v-if="addDocMode === 'file'">
                                            <label class="form-label">PDF / .txt / .md</label>
                                            <input
                                                type="file"
                                                class="form-control mb-2"
                                                accept=".pdf,.txt,.md,application/pdf,text/plain,text/markdown"
                                                ref="docFileInput"
                                                :disabled="addingDoc"
                                            />
                                            <input
                                                type="text"
                                                class="form-control mb-2"
                                                v-model="newDocTitle"
                                                placeholder="Title (optional — defaults to file name)"
                                                :disabled="addingDoc"
                                            />
                                            <button
                                                class="btn btn-primary btn-sm"
                                                :disabled="addingDoc"
                                                @click="submitFileDoc"
                                            >
                                                {{ addingDoc ? 'Uploading…' : 'Upload & Embed' }}
                                            </button>
                                        </div>

                                        <div v-else-if="addDocMode === 'url'">
                                            <label class="form-label">Web page or PDF URL</label>
                                            <input
                                                type="url"
                                                class="form-control mb-2"
                                                v-model="newDocUrl"
                                                placeholder="https://example.com/docs"
                                                :disabled="addingDoc"
                                            />
                                            <input
                                                type="text"
                                                class="form-control mb-2"
                                                v-model="newDocTitle"
                                                placeholder="Title (optional — defaults to page title)"
                                                :disabled="addingDoc"
                                            />
                                            <button
                                                class="btn btn-primary btn-sm"
                                                :disabled="addingDoc || !newDocUrl"
                                                @click="submitUrlDoc"
                                            >
                                                {{ addingDoc ? 'Fetching…' : 'Fetch & Embed' }}
                                            </button>
                                        </div>

                                    </div>
                                </div>
                            </div>

                        </div>

                        <!-- Action Buttons -->
                        <div class="d-flex justify-content-between align-items-center gap-3 mt-4 flex-wrap">
                            <!-- Clearing cached answers is a separate, low-stakes
                                 maintenance action — deliberately kept away from the
                                 Save/Reset pair so it isn't clicked by reflex. -->
                            <button
                                class="other-btn clear-cache-btn"
                                :disabled="clearingCache"
                                :title="clearCacheTooltip"
                                @click="clearCachedAnswers"
                            >
                                {{ clearingCache ? 'Clearing…' : 'Clear Cached Answers' }}
                            </button>
                            <div class="d-flex justify-content-end gap-3">
                                <button class="other-btn reset-btn" @click="resetToDefaults">
                                    Reset to Defaults
                                </button>
                                <button class="save-btn" :disabled="saving" @click="saveChatbot">
                                    {{ saving ? 'Saving...' : 'Save Configuration' }}
                                </button>
                            </div>
                        </div>
                        <p class="clear-cache-hint text-muted small mt-2 mb-0">
                            The chatbot reuses answers to repeated questions. Knowledge edits clear
                            them automatically — use this only if an answer still looks out of date.
                        </p>
                    </div>

                    <!-- Right Column - Preview (only show for UI tab) -->
                    <div class="preview-column" v-if="activeTab === 'ui'">
                        <div class="preview-sticky">
                            <h3 class="preview-title">Live Preview</h3>
                            <div class="preview-container">
                                <!-- FAB button preview -->
                                <div class="fab-preview-row">
                                    <div
                                        class="fab-preview-btn"
                                        :style="{ background: chatbotConfig.styling.primaryColor }"
                                    >
                                        <img
                                            v-if="chatbotConfig.welcomeMessage.avatarUrl"
                                            :src="chatbotConfig.welcomeMessage.avatarUrl"
                                            alt="Bot"
                                            @error="onAvatarImgError($event)"
                                        />
                                        <span v-else>💬</span>
                                    </div>
                                    <span class="fab-preview-label">Chat button</span>
                                </div>
                                <div
                                    class="chat-preview"
                                    :style="{
                                        '--primary-color': chatbotConfig.styling.primaryColor,
                                        '--secondary-color': chatbotConfig.styling.secondaryColor,
                                        '--text-color': chatbotConfig.styling.textColor,
                                        '--font-family': chatbotConfig.styling.fontFamily,
                                        '--font-size': chatbotConfig.styling.fontSize,
                                        '--border-radius': chatbotConfig.styling.borderRadius,
                                    }"
                                >
                                    <div class="chat-header">
                                        <div class="chat-avatar">
                                            <img v-if="chatbotConfig.welcomeMessage.avatarUrl" :src="chatbotConfig.welcomeMessage.avatarUrl" alt="Bot" @error="onAvatarImgError($event)" />
                                            <span v-else>🤖</span>
                                        </div>
                                        <div class="chat-header-info">
                                            <div class="chat-bot-name">{{ chatbotConfig.name || chatbotConfig.welcomeMessage.botName || 'Support Bot' }}</div>
                                            <div class="chat-bot-subtitle" v-if="chatbotConfig.welcomeMessage.title">{{ chatbotConfig.welcomeMessage.title }}</div>
                                        </div>
                                    </div>
                                    <div class="chat-body">
                                        <div class="chat-message bot-message">
                                            <div class="bot-msg-with-avatar">
                                                <div class="bot-msg-avatar">
                                                    <img v-if="chatbotConfig.welcomeMessage.avatarUrl" :src="chatbotConfig.welcomeMessage.avatarUrl" alt="Bot" @error="onAvatarImgError($event)" />
                                                    <span v-else>🤖</span>
                                                </div>
                                                <div>
                                                    <strong>{{ chatbotConfig.welcomeMessage.title || 'Welcome!' }}</strong>
                                                    <p>{{ chatbotConfig.welcomeMessage.message || 'How can I help you today?' }}</p>
                                                </div>
                                            </div>
                                        </div>
                                        <div class="chat-message user-message">
                                            <p>Hi, I have a question</p>
                                        </div>
                                        <div class="chat-message bot-message">
                                            <p>Sure! I'd be happy to help. What would you like to know?</p>
                                        </div>
                                    </div>
                                    <div class="chat-input">
                                        <input type="text" placeholder="Type a message..." disabled />
                                        <button disabled>Send</button>
                                    </div>
                                </div>
                            </div>
                            <p class="preview-hint">Preview updates in real-time as you configure</p>
                        </div>
                    </div>


                </div>
            </div>
        </div>
        <Loading v-if="loading || saving" />
    </div>
</template>

<script>
    import Loading from '@/components/general/loading/loading.vue';
    import KbCrawlPanel from './kb-crawl/KbCrawlPanel.vue';
    import DashboardKnowledgePanel from './dashboard-knowledge/DashboardKnowledgePanel.vue';

    export default {
        name: 'ChatbotBuilder',
        components: {
            Loading,
            KbCrawlPanel,
            DashboardKnowledgePanel,
        },
        data() {
            return {
                loading: false,
                saving: false,
                clearingCache: false,
                activeTab: 'ui',
                knowledgeTab: 'website',
                expandedSections: {
                    styling: true,
                    avatar: true,
                    faqs: true,
                    documentation: false,
                },
                chatbotConfig: {
                    name: '',
                    status: 'draft',
                    enabledDashboard: false,
                    enabledMobile: false,
                    scrapeFrequency: 'quarterly',
                    lastScrapedAt: null,
                    styling: {
                        primaryColor: '#5057c3',
                        secondaryColor: '#f1f7ff',
                        textColor: '#333333',
                        fontFamily: 'Inter, sans-serif',
                        fontSize: '14px',
                        borderRadius: '10px',
                        position: 'bottom-right',
                        buttonIcon: 'chat',
                    },
                    welcomeMessage: {
                        title: 'Welcome to Support',
                        message: 'Hello! How can I help you today?',
                        botName: 'Support Bot',
                        avatarUrl: '',
                    },
                    faqs: [],
                    documentation: [],
                },
                defaultConfig: null,
                // Add-doc panel state — docs are persisted per-row via dedicated
                // endpoints (POST /docs/upload | /docs/url | /docs/text) so a
                // config save never wipes uploaded files.
                addDocMode: 'file',
                addingDoc: false,
                newDocTitle: '',
                newDocUrl: '',
            };
        },
        computed: {
            clearCacheTooltip() {
                return 'Makes the chatbot regenerate answers from the latest knowledge instead of reusing stored ones. Affects every user in your company.';
            },
        },
        methods: {
            toggleSection(section) {
                this.expandedSections[section] = !this.expandedSections[section];
            },
            /**
             * Clear the company's stored chatbot answers.
             *
             * The server bumps a knowledge version rather than deleting keys, so
             * this is cheap and instant — every stored answer simply becomes stale
             * on its next read and is regenerated from current knowledge.
             */
            async clearCachedAnswers() {
                this.clearingCache = true;
                try {
                    const response = await this.$api.post('/chatbot-builder/cache/clear');
                    const message =
                        (response.data && response.data.message) ||
                        'Cached answers cleared.';
                    this.$toasted.success(message);
                } catch (e) {
                    const message =
                        (e && e.response && e.response.data && e.response.data.message) ||
                        'Could not clear cached answers.';
                    this.$toasted.error(message);
                } finally {
                    this.clearingCache = false;
                }
            },
            addFaq() {
                this.chatbotConfig.faqs.push({ question: '', answer: '' });
            },
            removeFaq(index) {
                this.chatbotConfig.faqs.splice(index, 1);
            },
            // ── Knowledge docs ──────────────────────────────────────────────
            docTypeLabel(doc) {
                if (!doc) return '';
                if (doc.type === 'pdf') return 'PDF';
                if (doc.type === 'url') return 'Link';
                if (doc.fileType === 'pdf') return 'PDF';
                return 'Text';
            },
            statusBadgeClass(status) {
                if (status === 'ready')      return 'badge-status-ready';
                if (status === 'processing') return 'badge-status-processing';
                if (status === 'failed')     return 'badge-status-failed';
                return 'badge-status-pending';
            },
            formatBytes(bytes) {
                if (!bytes) return '';
                const units = ['B', 'KB', 'MB', 'GB'];
                let i = 0; let n = bytes;
                while (n >= 1024 && i < units.length - 1) { n /= 1024; i++; }
                return `${n.toFixed(n < 10 && i ? 1 : 0)} ${units[i]}`;
            },
            upsertDocRow(saved) {
                const list = this.chatbotConfig.documentation;
                const idx = list.findIndex(d => d.id === saved.id);
                const row = { ...saved, _busy: false };
                if (idx >= 0) this.$set(list, idx, row);
                else list.push(row);
            },
            async submitFileDoc() {
                const input = this.$refs.docFileInput;
                const file = input && input.files && input.files[0];
                if (!file) { this.$toasted.error('Pick a file first'); return; }
                const fd = new FormData();
                fd.append('file', file);
                if (this.newDocTitle.trim()) fd.append('title', this.newDocTitle.trim());
                this.addingDoc = true;
                try {
                    const res = await this.$api.post('/chatbot-builder/docs/upload', fd);
                    if (res.data && res.data.success) {
                        this.upsertDocRow(res.data.data);
                        this.newDocTitle = '';
                        if (input) input.value = '';
                        this.$toasted.success('Document uploaded and indexed');
                    } else {
                        this.$toasted.error((res.data && res.data.message) || 'Upload failed');
                    }
                } catch (err) {
                    this.$toasted.error(err?.response?.data?.message || err.message || 'Upload failed');
                } finally {
                    this.addingDoc = false;
                }
            },
            async submitUrlDoc() {
                if (!this.newDocUrl.trim()) return;
                this.addingDoc = true;
                try {
                    const res = await this.$api.post('/chatbot-builder/docs/url', {
                        url:   this.newDocUrl.trim(),
                        title: this.newDocTitle.trim() || undefined,
                    });
                    if (res.data && res.data.success) {
                        this.upsertDocRow(res.data.data);
                        this.newDocUrl = '';
                        this.newDocTitle = '';
                        this.$toasted.success('Link fetched and indexed');
                    } else {
                        this.$toasted.error((res.data && res.data.message) || 'Failed to fetch URL');
                    }
                } catch (err) {
                    this.$toasted.error(err?.response?.data?.message || err.message || 'Failed to fetch URL');
                } finally {
                    this.addingDoc = false;
                }
            },
            async saveDocTitle(doc) {
                if (!doc || !doc.id) return;
                this.$set(doc, '_busy', true);
                try {
                    const res = await this.$api.put(`/chatbot-builder/docs/${doc.id}`, {
                        title: doc.title,
                    });
                    if (res.data && res.data.success) {
                        this.upsertDocRow(res.data.data);
                    }
                } catch (err) {
                    this.$toasted.error(err?.response?.data?.message || 'Failed to update title');
                } finally {
                    this.$set(doc, '_busy', false);
                }
            },
            async removeSavedDoc(doc) {
                if (!doc || !doc.id) return;
                if (!window.confirm(`Remove "${doc.title || 'this document'}"? It will stop appearing in chatbot answers.`)) return;
                this.$set(doc, '_busy', true);
                try {
                    const res = await this.$api.delete(`/chatbot-builder/docs/${doc.id}`);
                    if (res.data && res.data.success) {
                        this.chatbotConfig.documentation = this.chatbotConfig.documentation.filter(d => d.id !== doc.id);
                        this.$toasted.success('Document removed');
                    }
                } catch (err) {
                    this.$toasted.error(err?.response?.data?.message || 'Failed to remove document');
                    this.$set(doc, '_busy', false);
                }
            },

            clearAvatar() {
                this.chatbotConfig.welcomeMessage.avatarUrl = '';
            },

            onAvatarInput(value) {
                this.chatbotConfig.welcomeMessage.avatarUrl = (value || '').trim();
            },

            // Fallback when avatar image fails to load
            onAvatarImgError(event) {
                try {
                    event.target.onerror = null;
                    // Simple SVG fallback (small 'Bot' badge)
                    event.target.src = "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='64' height='64'><rect fill='%23f1f7ff' width='100%25' height='100%25'/><text x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' font-size='20' fill='%235057c3'>Bot</text></svg>";
                } catch (e) {
                    // noop
                }
            },
            resetToDefaults() {
                if (this.defaultConfig) {
                    this.chatbotConfig = JSON.parse(JSON.stringify(this.defaultConfig));
                    this.$toasted.success('Configuration reset to defaults');
                }
            },
            // KbCrawlPanel persists scrape settings independently via /kb-crawl/save.
            // Reflect the new values here so a subsequent "Save Configuration"
            // doesn't echo back stale defaults from the parent.
            onCrawlSaved({ scrapeFrequency, lastScrapedAt }) {
                if (scrapeFrequency) this.chatbotConfig.scrapeFrequency = scrapeFrequency;
                if (lastScrapedAt)   this.chatbotConfig.lastScrapedAt = lastScrapedAt;
            },
            applyConfig(saved) {
                const defaultStyling = this.chatbotConfig.styling;
                const defaultWelcome = this.chatbotConfig.welcomeMessage;
                const s = saved.styling || {};
                const w = saved.welcomeMessage || {};
                this.chatbotConfig = {
                    name: saved.name || '',
                    status: saved.status || 'draft',
                    enabledDashboard: saved.enabledDashboard === true,
                    enabledMobile: saved.enabledMobile === true,
                    scrapeFrequency: saved.scrapeFrequency || 'quarterly',
                    lastScrapedAt: saved.lastScrapedAt || null,
                    styling: {
                        primaryColor: s.primaryColor || defaultStyling.primaryColor,
                        secondaryColor: s.secondaryColor || defaultStyling.secondaryColor,
                        textColor: s.textColor || defaultStyling.textColor,
                        fontFamily: s.fontFamily || defaultStyling.fontFamily,
                        fontSize: s.fontSize || defaultStyling.fontSize,
                        borderRadius: s.borderRadius || defaultStyling.borderRadius,
                        position: s.position || defaultStyling.position,
                        buttonIcon: s.buttonIcon || defaultStyling.buttonIcon,
                    },
                    welcomeMessage: {
                        title: w.title || defaultWelcome.title,
                        message: w.message || defaultWelcome.message,
                        botName: w.botName || defaultWelcome.botName,
                        avatarUrl: w.avatarUrl || '',
                    },
                    faqs: saved.faqs || [],
                    documentation: (saved.documentation || []).map(d => ({ ...d, _busy: false })),
                };
                this.defaultConfig = JSON.parse(JSON.stringify(this.chatbotConfig));
            },
            async loadChatbot() {
                this.loading = true;
                try {
                    const response = await this.$api.get('/chatbot-builder/config');
                    if (response.data && response.data.data) {
                        this.applyConfig(response.data.data);
                    }
                } catch (err) {
                    const msg = err?.response?.data?.message || err.message || 'Failed to load chatbot configuration';
                    this.$toasted.error(msg);
                } finally {
                    this.loading = false;
                }
            },
            async saveChatbot() {
                if (!this.chatbotConfig.name.trim()) {
                    this.$toasted.error('Chatbot name is required');
                    return;
                }

                this.saving = true;
                try {
                    const payload = {
                        name: this.chatbotConfig.name,
                        status: this.chatbotConfig.status,
                        enabledDashboard: this.chatbotConfig.enabledDashboard,
                        enabledMobile: this.chatbotConfig.enabledMobile,
                        styling: this.chatbotConfig.styling,
                        welcomeMessage: this.chatbotConfig.welcomeMessage,
                        faqs: this.chatbotConfig.faqs,
                        // documentation is intentionally omitted — docs are managed
                        // per-row via /chatbot-builder/docs/* endpoints so a config
                        // save never re-uploads or re-fetches them.
                        // qna was merged into faqs in migration 1.78.1.
                    };
                    const response = await this.$api.post('/chatbot-builder/config', payload);
                    if (response.data && response.data.data) {
                        this.applyConfig(response.data.data);
                    }
                    this.$toasted.success('Chatbot configuration saved successfully!');
                } catch (err) {
                    const msg = err?.response?.data?.message || err.message || 'Failed to save chatbot configuration';
                    this.$toasted.error(msg);
                } finally {
                    this.saving = false;
                }
            },
        },
        async created() {
            // Store default config for reset functionality
            this.defaultConfig = JSON.parse(JSON.stringify(this.chatbotConfig));
            await this.loadChatbot();
        },
    };
</script>

<style scoped>
    .bg-color {
        background-color: rgba(255, 255, 255, 0.9);
    }

    .list-page-header {
        display: block;
        margin: 0;
        padding: 0;
        background: none;
    }

    /* Chatbot Toggle Section */
    .chatbot-toggle-section {
        margin-bottom: 20px;
    }

    .toggle-card {
        display: flex;
        justify-content: space-between;
        align-items: center;
        background: linear-gradient(135deg, #f8f9ff 0%, #fff 100%);
        border: 1px solid #e5e9f2;
        border-radius: 12px;
        padding: 20px 25px;
    }

    .toggle-info {
        flex: 1;
    }

    .toggle-title {
        margin: 0 0 5px 0;
        font-size: 1.1rem;
        font-weight: 600;
        color: #333;
    }

    .toggle-description {
        margin: 0;
        font-size: 0.9rem;
        color: #666;
    }

    .toggle-switch-wrapper {
        display: flex;
        align-items: center;
        gap: 12px;
    }

    .toggle-label {
        font-size: 0.9rem;
        font-weight: 500;
        color: #999;
        transition: color 0.2s ease;
    }

    .toggle-label.active {
        color: #28a745;
    }

    .toggle-label.inactive {
        color: #999;
    }

    .toggle-switch {
        position: relative;
        display: inline-block;
        width: 56px;
        height: 30px;
    }

    .toggle-switch input {
        opacity: 0;
        width: 0;
        height: 0;
    }

    .toggle-slider {
        position: absolute;
        cursor: pointer;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background-color: #ccc;
        transition: 0.3s;
        border-radius: 30px;
    }

    .toggle-slider:before {
        position: absolute;
        content: "";
        height: 22px;
        width: 22px;
        left: 4px;
        bottom: 4px;
        background-color: white;
        transition: 0.3s;
        border-radius: 50%;
        box-shadow: 0 2px 4px rgba(0,0,0,0.2);
    }

    .toggle-switch input:checked + .toggle-slider {
        background-color: #28a745;
    }

    .toggle-switch input:checked + .toggle-slider:before {
        transform: translateX(26px);
    }

    /* Two Column Layout */
    .main-content-wrapper {
        display: flex;
        gap: 30px;
        align-items: flex-start;
    }

    .config-column {
        flex: 1;
        min-width: 0;
    }

    .preview-column {
        width: 380px;
        flex-shrink: 0;
    }

    .preview-sticky {
        position: sticky;
        top: 100px;
    }

    .preview-title {
        font-size: 1.1rem;
        font-weight: 600;
        color: #333;
        margin-bottom: 15px;
        text-align: center;
    }

    .preview-hint {
        text-align: center;
        color: #888;
        font-size: 0.85rem;
        margin-top: 10px;
    }

    .config-section {
        margin-bottom: 1rem;
    }

    /* Guaranteed two-up layout — independent of the project's global Bootstrap
       grid CSS, which has a `.row > *` rule that fights with `.col-6`. */
    .field-row {
        display: flex;
        gap: 1rem;
        align-items: stretch;
        flex-wrap: nowrap;
    }

    .field-row > .field-col {
        flex: 1 1 0;
        min-width: 0;
    }

    @media (max-width: 600px) {
        .field-row {
            flex-wrap: wrap;
        }
        .field-row > .field-col {
            flex: 1 1 100%;
        }
    }

    .section-heading {
        background: #f1f7ff;
        font-weight: bold;
        font-size: 1rem;
        border-radius: 14px 14px 0 0;
        margin: 0;
        cursor: pointer;
        user-select: none;
    }

    .section-heading:hover {
        background: #e5effc;
    }

    .toggle-icon {
        font-size: 1.5rem;
        color: #5057c3;
    }

    .styling-grid {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: 20px;
    }

    .styling-field {
        min-width: 0;
    }

    .color-input-wrapper {
        display: flex;
        gap: 10px;
        align-items: center;
    }

    .color-input-wrapper input[type="color"] {
        width: 50px;
        height: 38px;
        padding: 2px;
        cursor: pointer;
    }

    .color-input-wrapper input[type="text"] {
        flex: 1;
    }

    .btn-add {
        display: inline-flex;
        align-items: center;
        gap: 8px;
        padding: 10px 20px;
        background-color: #5057c3;
        color: white;
        border: none;
        border-radius: 8px;
        cursor: pointer;
        font-weight: 600;
    }

    .btn-add:hover {
        background-color: #3f45a0;
    }

    .btn-remove {
        background: none;
        border: none;
        cursor: pointer;
        padding: 5px;
    }

    .btn-remove:hover {
        opacity: 0.7;
    }

    .faq-item,
    .doc-item {
        background-color: #fafbfc;
    }

    .add-doc-panel {
        background-color: #fafbfc;
    }

    .badge-doc-type {
        background-color: #eef1f7;
        color: #4b5563;
        font-weight: 600;
        font-size: 11px;
        padding: 2px 8px;
        text-transform: uppercase;
        letter-spacing: 0.5px;
    }

    .badge-status-ready      { background-color: #d1fae5; color: #065f46; font-size: 11px; padding: 2px 8px; }
    .badge-status-processing { background-color: #fef3c7; color: #92400e; font-size: 11px; padding: 2px 8px; }
    .badge-status-failed     { background-color: #fee2e2; color: #991b1b; font-size: 11px; padding: 2px 8px; }
    .badge-status-pending    { background-color: #e5e7eb; color: #4b5563; font-size: 11px; padding: 2px 8px; }

    /* Context Card Styles */
    .context-card {
        background-color: #fafbfc;
        border: 1px solid #e5e9f2;
        transition: box-shadow 0.2s ease;
    }

    .context-card:hover {
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    }

    .context-card:first-child {
        border-left: 3px solid #5057c3;
    }

    .context-card:not(:first-child) {
        border-left: 3px solid #6c757d;
    }

    .context-card .badge {
        font-size: 0.75rem;
        padding: 4px 10px;
        border-radius: 12px;
    }

    /* Prevent inputs inside context cards from overflowing their container */
    .context-card .row > [class*="col-"] {
        min-width: 0; /* allow columns to shrink inside flex/grid */
    }

    .context-card input,
    .context-card textarea,
    .context-card select {
        width: 100%;
        box-sizing: border-box;
        overflow-wrap: break-word;
        word-break: break-word;
    }

    /* Allow header controls to wrap instead of pushing content out */
    .context-card .d-flex {
        flex-wrap: wrap;
        gap: 0.5rem;
    }

    /* Ensure badges and small inline elements don't force overflow */
    .context-card .badge {
        white-space: nowrap;
        max-width: 100%;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    .gap-3 {
        gap: 1rem;
    }

    .btn-add:disabled {
        background-color: #ccc;
        cursor: not-allowed;
    }

    .save-btn {
        padding: 10px 30px;
        background-color: #5057c3;
        color: white;
        border: none;
        border-radius: 8px;
        cursor: pointer;
        font-weight: 600;
    }

    .save-btn:hover:not(:disabled) {
        background-color: #3f45a0;
    }

    .save-btn:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }

    .reset-btn {
        padding: 10px 30px;
        background: white;
        border: 1px solid #ddd;
        border-radius: 8px;
        cursor: pointer;
    }

    .reset-btn:hover {
        background: #f5f5f5;
    }

    /* Maintenance action — visually quieter than Reset/Save so it doesn't
       compete with them for attention. */
    .clear-cache-btn {
        padding: 10px 20px;
        background: white;
        border: 1px solid #e0e0e0;
        border-radius: 8px;
        color: #6c757d;
        cursor: pointer;
    }

    .clear-cache-btn:hover:not(:disabled) {
        background: #f8f9fa;
        color: #495057;
    }

    .clear-cache-btn:disabled {
        opacity: 0.6;
        cursor: default;
    }

    .clear-cache-hint {
        max-width: 640px;
    }

    /* Preview Styles */
    .preview-container {
        background: #f5f5f5;
        border-radius: 10px;
        padding: 20px;
    }

    .chat-preview {
        width: 100%;
        border-radius: var(--border-radius);
        overflow: hidden;
        box-shadow: 0 5px 20px rgba(0, 0, 0, 0.15);
        font-family: var(--font-family);
        font-size: var(--font-size);
    }

    .chat-header {
        background: var(--primary-color);
        color: white;
        padding: 15px;
        display: flex;
        align-items: center;
        gap: 10px;
    }

    .chat-avatar {
        width: 40px;
        height: 40px;
        border-radius: 50%;
        background: white;
        display: flex;
        align-items: center;
        justify-content: center;
        overflow: hidden;
    }

    .chat-avatar img {
        width: 100%;
        height: 100%;
        object-fit: cover;
    }

    .chat-bot-name {
        font-weight: 600;
    }

    .chat-header-info {
        display: flex;
        flex-direction: column;
        flex: 1;
        min-width: 0;
    }

    .chat-bot-subtitle {
        font-size: 11px;
        opacity: 0.8;
        margin-top: 2px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    .bot-msg-with-avatar {
        display: flex;
        align-items: flex-start;
        gap: 8px;
    }

    .bot-msg-avatar {
        width: 28px;
        height: 28px;
        border-radius: 50%;
        background: var(--primary-color);
        display: flex;
        align-items: center;
        justify-content: center;
        overflow: hidden;
        flex-shrink: 0;
        font-size: 14px;
    }

    .bot-msg-avatar img {
        width: 100%;
        height: 100%;
        object-fit: cover;
    }

    .fab-preview-row {
        display: flex;
        align-items: center;
        gap: 10px;
        padding: 10px 0 4px 0;
    }

    .fab-preview-btn {
        width: 44px;
        height: 44px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        overflow: hidden;
        cursor: pointer;
        box-shadow: 0 2px 8px rgba(0,0,0,0.25);
        font-size: 20px;
        flex-shrink: 0;
    }

    .fab-preview-btn img {
        width: 100%;
        height: 100%;
        object-fit: cover;
    }

    .fab-preview-label {
        font-size: 12px;
        color: #888;
    }

    .chat-body {
        background: var(--secondary-color);
        padding: 15px;
        min-height: 200px;
        max-height: 280px;
        overflow-y: auto;
    }

    .chat-message {
        margin-bottom: 10px;
        padding: 10px 15px;
        border-radius: var(--border-radius);
        max-width: 85%;
    }

    .chat-message p {
        margin: 0;
    }

    .bot-message {
        background: #fff;
        color: var(--text-color);
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    }

    .user-message {
        background: var(--primary-color);
        color: #fff;
        margin-left: auto;
    }

    .chat-input {
        background: white;
        padding: 10px 15px;
        border-top: 1px solid #eee;
        display: flex;
        gap: 10px;
    }

    .chat-input input {
        flex: 1;
        padding: 10px;
        border: 1px solid #ddd;
        border-radius: var(--border-radius);
        font-family: var(--font-family);
        font-size: var(--font-size);
    }

    .chat-input button {
        padding: 10px 20px;
        background: var(--primary-color);
        color: white;
        border: none;
        border-radius: var(--border-radius);
        cursor: pointer;
    }

    /* Responsive */
    @media (max-width: 1200px) {
        .main-content-wrapper {
            flex-direction: column;
        }

        .preview-column {
            width: 100%;
            order: -1;
        }

        .preview-sticky {
            position: static;
        }
    }

    /* Tabs Styling */
    .tabs-wrapper {
        border-bottom: 2px solid #e9ecef;
    }

    .tabs-nav {
        display: flex;
        gap: 5px;
    }

    .tab-btn {
        padding: 12px 24px;
        background: none;
        border: none;
        border-bottom: 3px solid transparent;
        cursor: pointer;
        font-size: 0.95rem;
        font-weight: 500;
        color: #666;
        display: flex;
        align-items: center;
        gap: 8px;
        transition: all 0.2s ease;
        margin-bottom: -2px;
    }

    .tab-btn:hover {
        color: #5057c3;
        background: #f8f9ff;
    }

    .tab-btn.active {
        color: #5057c3;
        border-bottom-color: #5057c3;
        background: #f1f7ff;
    }

    /* Sub-Tabs (inside Knowledge tab) */
    .sub-tabs-wrapper {
        background: #f8f9ff;
        border: 1px solid #e5e9f2;
        border-radius: 12px;
        padding: 6px;
    }

    .sub-tabs-nav {
        display: flex;
        gap: 4px;
        flex-wrap: wrap;
    }

    .sub-tab-btn {
        flex: 1;
        min-width: 140px;
        padding: 10px 16px;
        background: transparent;
        border: none;
        border-radius: 8px;
        cursor: pointer;
        font-size: 0.9rem;
        font-weight: 500;
        color: #666;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
        transition: all 0.2s ease;
    }

    .sub-tab-btn:hover {
        color: #5057c3;
        background: #fff;
    }

    .sub-tab-btn.active {
        color: #fff;
        background: #5057c3;
        box-shadow: 0 2px 6px rgba(80, 87, 195, 0.25);
    }

    @media (max-width: 768px) {
        .sub-tab-btn {
            min-width: 0;
            font-size: 0.85rem;
            padding: 8px 12px;
        }
    }

    /* Bot avatar input + clear button laid out on a single row */
    .avatar-input-row {
        display: flex;
        gap: 8px;
        align-items: center;
    }

    .avatar-input-row .form-control {
        flex: 1;
    }

    .avatar-btn.clear-btn {
        border: 1px solid #e5e9f2;
        padding: 6px 14px;
        height: 38px;
        border-radius: 8px;
        font-size: 0.85rem;
        color: #5057c3;
        background: #f1f7ff;
        cursor: pointer;
        white-space: nowrap;
    }

    .avatar-btn.clear-btn:hover {
        background: #e5effc;
    }

    /* Conversation Detail Styles */
    .conversation-detail-column {
        width: 450px;
    }

    .conversation-detail {
        background: #fff;
        border: 1px solid #e5e9f2;
        border-radius: 10px;
        overflow: hidden;
    }

    .detail-header {
        background: #f1f7ff;
        padding: 15px 20px;
        display: flex;
        justify-content: space-between;
        align-items: center;
        border-bottom: 1px solid #e5e9f2;
    }

    .detail-messages {
        padding: 20px;
        max-height: 450px;
        overflow-y: auto;
    }

    .detail-message {
        margin-bottom: 15px;
        max-width: 80%;
    }

    .detail-message.bot {
        margin-right: auto;
    }

    .detail-message.user {
        margin-left: auto;
        text-align: right;
    }

    .msg-content {
        padding: 10px 15px;
        border-radius: 12px;
        display: inline-block;
    }

    .detail-message.bot .msg-content {
        background: #f1f7ff;
        color: #333;
    }

    .detail-message.user .msg-content {
        background: #5057c3;
        color: white;
    }

    .msg-time {
        font-size: 0.7rem;
        color: #999;
        margin-top: 4px;
    }

    /* Dashboard Settings Styles */
    .dashboard-settings-container {
        min-height: 400px;
    }

    .dashboard-info-banner {
        display: flex;
        gap: 15px;
        align-items: flex-start;
        background: linear-gradient(135deg, #f0f4ff 0%, #e8f0fe 100%);
        border: 1px solid #c5d5f0;
        border-radius: 12px;
        padding: 20px;
    }

    .dashboard-info-banner .info-icon {
        font-size: 1.5rem;
        flex-shrink: 0;
    }

    .dashboard-info-banner .info-content p {
        margin: 5px 0 0;
        font-size: 0.9rem;
        color: #555;
        line-height: 1.5;
    }

    .dashboard-list {
        border: 1px solid #e5e9f2;
        border-radius: 12px;
        overflow: hidden;
    }

    .dashboard-item {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 16px 20px;
        border-bottom: 1px solid #e5e9f2;
        transition: background 0.2s ease;
    }

    .dashboard-item:last-child {
        border-bottom: none;
    }

    .dashboard-item:hover {
        background: #f8f9ff;
    }

    .dashboard-item.is-enabled {
        border-left: 3px solid #28a745;
    }

    .dashboard-item-left {
        display: flex;
        align-items: center;
        gap: 15px;
        flex: 1;
    }

    .dashboard-icon {
        font-size: 1.3rem;
        width: 36px;
        height: 36px;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 8px;
        background: #f1f7ff;
    }

    .dashboard-details {
        flex: 1;
    }

    .dashboard-name {
        font-weight: 600;
        font-size: 0.95rem;
        color: #333;
    }

    .dashboard-meta {
        font-size: 0.8rem;
        color: #888;
        margin-top: 2px;
    }

    .dashboard-item-right {
        display: flex;
        align-items: center;
        gap: 20px;
    }

    .dashboard-status {
        display: flex;
        align-items: center;
        gap: 6px;
    }

    .status-dot {
        width: 8px;
        height: 8px;
        border-radius: 50%;
    }

    .status-dot.active {
        background: #28a745;
    }

    .status-dot.inactive {
        background: #ccc;
    }

    .status-text {
        font-size: 0.85rem;
        font-weight: 500;
        color: #555;
    }

    .dashboard-summary {
        display: flex;
        gap: 20px;
    }

    .summary-item {
        flex: 1;
        background: #f8f9ff;
        border: 1px solid #e5e9f2;
        border-radius: 10px;
        padding: 18px 20px;
        text-align: center;
    }

    .summary-count {
        display: block;
        font-size: 1.8rem;
        font-weight: 700;
        color: #5057c3;
    }

    .summary-label {
        font-size: 0.85rem;
        color: #666;
    }

    @media (max-width: 992px) {
        .tabs-nav {
            flex-wrap: wrap;
        }

        .tab-btn {
            padding: 10px 16px;
            font-size: 0.9rem;
        }
    }
</style>
